import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { doc, getDoc, deleteDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { FaTrash } from "react-icons/fa";
import {
  selectChatHistory,
  selectIsLoading,
  selectUser,
  selectTilesDetails,
  fetchData,
  selectRestrictedDataError,
  selectChatHistoryVersion,
} from "../../../mainApp/features/DataSlice/dataSlice";
import {
  ChatbotContainer,
  ChatbotMain,
  ChatbotHistoryDetailsPanel,
  ChatbotSuggestions,
  SuggestionsHeader,
  SuggestionsBox,
  SuggestionItem,
  SuggestionTitle,
  SuggestionDescription,
  ChatbotMessage,
  AppTitle,
  ChatbotExpandButton,
  ChatbotDetailsHeader,
  ChatbotSection,
  ChatbotSectionTitle,
  HistoryTile,
  HistorySection,
  HistorySectionTitle,
  HistoryTileWrapper,
  EntryDetailsContainer,
  EntryDetailsTitle,
  LoadingElement,
  ChatbotName,
  SuggestionLink,
  HistoryHeaderContainer,
  HistoryActionsContainer,
  SelectAllButton,
  DeleteButton,
  HistoryTileCheckbox,
  HistoryTileContent,
  CategorySelect,
} from "../Bots/styled";
import DeleteConfirmModal from "./DeleteConfirmModal";
import LoadingPage from "../TechnicalTabs/LoadingPage";
import ErrorPage from "../TechnicalTabs/ErrorPage";
import { handleUnauthorizedAccess } from "../Utility/handleUnauthorizedAccess";
import { onAuthStateChanged } from "firebase/auth";
import { MusicPlayer, MultiMusicPlayer } from '../Bots/MusicCreator/Chatbot';

const renderClickableText = (text) => {
  if (typeof text !== 'string') return text;
  
  const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = markdownLinkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    const [fullMatch, linkText, url] = match;
    parts.push(
      <SuggestionLink key={url + lastIndex} href={url} target="_blank" rel="noopener noreferrer">
        {linkText}
      </SuggestionLink>
    );
    lastIndex = match.index + fullMatch.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.map((part, index) => <React.Fragment key={index}>{part}</React.Fragment>);
};

const HistoryImage = ({ src, alt }) => {
  const [broken, setBroken] = useState(false);

  if (broken) {
    return (
      <ChatbotMessage style={{ textAlign: "left", color: "#ff4d4d" }}>
        Obraz usunięty. Uwaga, obrazy zawsze usuwane są cyklicznie co 60 dni. Pamiętaj by zapisywać je na dysku.
      </ChatbotMessage>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      style={{
        maxWidth: "100%",
        maxHeight: "80vh",
        objectFit: "contain",
        display: "block",
        margin: 0,
      }}
      onError={() => setBroken(true)}
    />
  );
};

const HistoryVideo = ({ src }) => {
  const [broken, setBroken] = useState(false);

  if (broken) {
    return (
      <ChatbotMessage style={{ textAlign: "left", color: "#ff4d4d" }}>
        Wideo usunięte. Uwaga, materiały wideo zawsze usuwane są cyklicznie co 60 dni. Pamiętaj by zapisywać je na dysku.
      </ChatbotMessage>
    );
  }

  return (
    <video
      controls
      style={{
        maxWidth: "100%",
        maxHeight: "300px",
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
      }}
      onError={() => setBroken(true)}
    >
      <source src={src} type="video/mp4" />
      Twoja przeglądarka nie obsługuje odtwarzania wideo.
    </video>
  );
};


const ChatbotHistory = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const chatHistory = useSelector(selectChatHistory);
  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const tilesDetails = useSelector(selectTilesDetails);
  const restrictedDataError = useSelector(selectRestrictedDataError);
  const chatHistoryVersion = useSelector(selectChatHistoryVersion);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [entryDetails, setEntryDetails] = useState(null);
  const [entryLoading, setEntryLoading] = useState(false);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");

const isMusicUrl = (content) => {
  return /https:\/\/storage\.googleapis\.com\/[^\s]+\.(mp3|wav|m4a|ogg)([^\s]*)?/i.test(content);
};

  const chatbots = [
    { id: "reels", name: "Filmiki i Rolki" },
    { id: "lawyer", name: "Asystent Prawny" },
    { id: "casual", name: "Asystent Ogólny" },
    { id: "facebook", name: "Posty Facebook" },
    { id: "advanced", name: "Social Media" },
    { id: "image", name: "Lokalny Artysta" },
    { id: "video", name: "Generator Wideo" },
    { id: "music", name: "Asystent Muzyczny" },
    { id: "diet", name: "Doradca Dietetyczny" },
    { id: "searchengine", name: "Wyszukiwarka" },
    { id: "advertisement", name: "Twórca Ogłoszeń" },
    { id: "articles", name: "Twórca Artykułów" },
  ];

  // Funkcja opóźnienia
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    dispatch(fetchData());
    dispatch({ type: "FETCH_CHAT_HISTORY" });
  }, [dispatch, chatHistoryVersion]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          dispatch(fetchData());
          const ref = doc(db, "Users", currentUser.uid);
          const snap = await getDoc(ref);
          if (!snap.exists()) {
            console.warn("Dokument użytkownika nie istnieje.");
          }
          await delay(600); // Dodatkowe opóźnienie jako zabezpieczenie
        } catch (error) {
          console.error("Błąd pobierania danych użytkownika:", error);
          await delay(600); // Opóźnienie w przypadku błędu
        }
      } else {
        navigate("/login");
      }
      setInitialLoading(false);
    });
    return () => unsubscribe();
  }, [dispatch, navigate]);

  useEffect(() => {
    if (restrictedDataError) {
      navigate("/unauthorized");
    }
  }, [restrictedDataError, navigate]);

  const fetchEntryDetails = async (entryId) => {
    setEntryLoading(true);
    try {
      const user = auth.currentUser;
      if (user) {
        const docRef = doc(db, `Users/${user.uid}/chatHistory`, entryId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setEntryDetails({ id: docSnap.id, ...docSnap.data() });
        } else {
          setEntryDetails(null);
          console.warn("Dokument wpisu nie istnieje.");
        }
      }
      await delay(600); // Opóźnienie jako zabezpieczenie
    } catch (error) {
      console.error("Błąd pobierania wpisu:", error);
      setEntryDetails(null);
      await delay(600); // Opóźnienie w przypadku błędu
    }
    setEntryLoading(false);
  };

  const handleTileClick = (entry, event) => {
    // Jeśli kliknięto w checkbox, nie otwieraj szczegółów
    if (event && (event.target.type === 'checkbox' || event.target.closest('input[type="checkbox"]'))) {
      return;
    }
    setSelectedEntry(entry);
    fetchEntryDetails(entry.id);
    setIsDetailsExpanded(true);
  };

  const toggleDetailsPanel = () => setIsDetailsExpanded((prev) => !prev);

  const handleCheckboxChange = (entryId, checked) => {
    setSelectedEntries((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(entryId);
      } else {
        newSet.delete(entryId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const historyToUse = filteredHistory;
    const allSelected = historyToUse.every((entry) => selectedEntries.has(entry.id));
    
    if (allSelected && historyToUse.length > 0) {
      // Odznacz wszystkie z filtrowanej listy
      setSelectedEntries((prev) => {
        const newSet = new Set(prev);
        historyToUse.forEach((entry) => newSet.delete(entry.id));
        return newSet;
      });
    } else {
      // Zaznacz wszystkie z filtrowanej listy
      setSelectedEntries((prev) => {
        const newSet = new Set(prev);
        historyToUse.forEach((entry) => newSet.add(entry.id));
        return newSet;
      });
    }
  };

  const handleDeleteClick = () => {
    if (selectedEntries.size === 0) return;
    
    // Sprawdź czy użytkownik już widział ostrzeżenie
    const hasSeenWarning = localStorage.getItem('chatHistoryDeleteWarningSeen') === 'true';
    
    // Pokaż modal tylko jeśli:
    // 1. To pierwszy raz (nie ma w localStorage)
    // 2. Zaznaczono wszystkie wpisy
    const shouldShowModal = !hasSeenWarning || selectedEntries.size === chatHistory.length;
    
    if (shouldShowModal) {
      setShowDeleteModal(true);
    } else {
      // Usuń bezpośrednio bez modala
      handleDeleteDirectly();
    }
  };

  const handleDeleteDirectly = async () => {
    setIsDeleting(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        console.error("Użytkownik nie jest zalogowany");
        return;
      }
      
      // Usuń zaznaczone wpisy z Firebase
      const deletePromises = Array.from(selectedEntries).map((entryId) => {
        const docRef = doc(db, `Users/${user.uid}/chatHistory`, entryId);
        return deleteDoc(docRef);
      });

      await Promise.all(deletePromises);

      // Jeśli usunięto aktualnie wybrany wpis, wyczyść szczegóły
      if (selectedEntry && selectedEntries.has(selectedEntry.id)) {
        setSelectedEntry(null);
        setEntryDetails(null);
        setIsDetailsExpanded(false);
      }

      // Wyczyść zaznaczenia
      setSelectedEntries(new Set());
      
      // Odśwież historię
      dispatch({ type: "FETCH_CHAT_HISTORY" });
      dispatch(fetchData());
    } catch (error) {
      console.error("Błąd podczas usuwania wpisów:", error);
      alert("Wystąpił błąd podczas usuwania wpisów. Spróbuj ponownie.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    // Zapisz w localStorage że użytkownik już widział ostrzeżenie
    localStorage.setItem('chatHistoryDeleteWarningSeen', 'true');
    
    // Zamknij modal i usuń
    setShowDeleteModal(false);
    await handleDeleteDirectly();
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    // Wyczyść zaznaczenia przy zmianie kategorii
    setSelectedEntries(new Set());
  };

  // Filtruj historię na podstawie wybranej kategorii
  const filteredHistory = React.useMemo(() => {
    if (!chatHistory) return [];
    return selectedCategory === "all" 
      ? chatHistory 
      : chatHistory.filter((entry) => entry.chatbotId === selectedCategory);
  }, [chatHistory, selectedCategory]);

  const renderReelsContent = (content) => {
    const contentToRender = (content && typeof content === 'object' && content.content) ? content.content : content;
    try {
      const suggestions = JSON.parse(contentToRender);
      if (Array.isArray(suggestions)) {
        return (
          <ChatbotSuggestions>
            <SuggestionsHeader>Propozycje filmików:</SuggestionsHeader>
            <SuggestionsBox>
              {suggestions.map((suggestion, index) => (
                <SuggestionItem key={index} isHistory={true}>
                  <SuggestionTitle>{suggestion.title}</SuggestionTitle>
                  <SuggestionDescription>
                    {suggestion.description}
                  </SuggestionDescription>
                </SuggestionItem>
              ))}
            </SuggestionsBox>
          </ChatbotSuggestions>
        );
      }
      return <ChatbotMessage isReels={true}>{contentToRender}</ChatbotMessage>;
    } catch (e) {
      return <ChatbotMessage isReels={true}>{contentToRender}</ChatbotMessage>;
    }
  };
  
  const renderSearchEngineContent = (content) => {
    const contentToRender = (content && typeof content === 'object' && content.content) ? content.content : content;
    try {
      const suggestions = JSON.parse(contentToRender);
      if (Array.isArray(suggestions)) {
        return (
          <ChatbotSuggestions>
            <SuggestionsHeader>Wyniki wyszukiwania:</SuggestionsHeader>
            <SuggestionsBox>
              {suggestions.map((suggestion, index) => (
                <SuggestionItem key={index} isHistory={true}>
                  <SuggestionTitle>{suggestion.title}</SuggestionTitle>
                  <SuggestionDescription>
                    {suggestion.description}
                  </SuggestionDescription>
                   <SuggestionLink href={suggestion.link} target="_blank" rel="noopener noreferrer">
                    {suggestion.link}
                  </SuggestionLink>
                </SuggestionItem>
              ))}
            </SuggestionsBox>
          </ChatbotSuggestions>
        );
      }
      return <ChatbotMessage>{renderClickableText(contentToRender)}</ChatbotMessage>;
    } catch (e) {
      return <ChatbotMessage>{renderClickableText(contentToRender)}</ChatbotMessage>;
    }
  };

  const renderDietContent = (content) => {
    const contentToRender = (content && typeof content === 'object' && content.content) ? content.content : content;
    try {
      const suggestions = JSON.parse(contentToRender);
      if (Array.isArray(suggestions)) {
        return (
          <ChatbotSuggestions>
            <SuggestionsHeader>Proponowany plan na 10 dni:</SuggestionsHeader>
            <SuggestionsBox>
              {suggestions.map((suggestion, index) => (
                <SuggestionItem key={index} isHistory={true}>
                  <SuggestionTitle>{suggestion.title}</SuggestionTitle>
                  <SuggestionDescription>
                    {suggestion.description}
                  </SuggestionDescription>
                </SuggestionItem>
              ))}
            </SuggestionsBox>
          </ChatbotSuggestions>
        );
      }
      return <div dangerouslySetInnerHTML={{ __html: contentToRender }} />;
    } catch (e) {
      return <div dangerouslySetInnerHTML={{ __html: contentToRender }} />;
    }
  };

  const renderLawyerContent = (content) => {
    const contentToRender = (content && typeof content === 'object' && content.content) ? content.content : content;
    try {
      // Spróbuj sparsować jako JSON (historia rozmowy)
      if (typeof contentToRender === 'string') {
        const messages = JSON.parse(contentToRender);
        if (Array.isArray(messages)) {
          return messages.map((msg, index) => {
            // Pomijamy początkową wiadomość systemową jeśli istnieje
            if (index === 0 && !msg.isUser && (msg.text.includes("Zadaj pytanie") || msg.text.includes("pytanie prawne"))) {
              return null;
            }
            return (
              <ChatbotMessage
                key={index}
                isLawyer={true}
                style={{
                  backgroundColor: msg.isUser ? "#536b8b" : "#2d4356", // lawyerLightGrey : lawyerDarkGrey
                  marginLeft: msg.isUser ? "0" : "20px",
                  marginRight: msg.isUser ? "20px" : "0",
                  maxWidth: msg.isUser ? "80%" : "100%",
                  color: "#ffffff",
                }}
              >
                {msg.text}
              </ChatbotMessage>
            );
          }).filter(Boolean); // Usuwamy null wartości
        }
      }
      // Fallback dla starego formatu (pojedyncza odpowiedź jako string)
      return (
        <ChatbotMessage
          isLawyer={true}
          style={{
            backgroundColor: "#2d4356", // lawyerDarkGrey
            marginLeft: "20px",
            color: "#ffffff",
          }}
        >
          {contentToRender}
        </ChatbotMessage>
      );
    } catch (e) {
      // Jeśli nie jest JSON, traktuj jako zwykły tekst (stary format)
      return (
        <ChatbotMessage
          isLawyer={true}
          style={{
            backgroundColor: "#2d4356", // lawyerDarkGrey
            marginLeft: "20px",
            color: "#ffffff",
          }}
        >
          {contentToRender}
        </ChatbotMessage>
      );
    }
  };

  const renderCasualContent = (content) => {
    const contentToRender = (content && typeof content === 'object' && content.content) ? content.content : content;
    try {
      // Jeżeli po rozpakowaniu content jest stringiem, spróbuj go sparsować
      if (typeof contentToRender !== 'string') {
         return (
            <ChatbotMessage
              isCasual={true}
              style={{ backgroundColor: "#2a2a2a", marginLeft: "20px" }}
            >
              {contentToRender}
            </ChatbotMessage>
          );
      }

      const messages = JSON.parse(contentToRender);
      if (Array.isArray(messages)) {
        return messages.map((msg, index) => (
          <ChatbotMessage
            key={index}
            isCasual={true}
            style={{
              backgroundColor: msg.isUser ? "#1db954" : "#2a2a2a",
              marginLeft: msg.isUser ? "auto" : "20px",
              marginRight: msg.isUser ? "20px" : "auto",
              maxWidth: "80%",
            }}
          >
            {msg.text}
          </ChatbotMessage>
        ));
      }
      return (
        <ChatbotMessage
          isCasual={true}
          style={{ backgroundColor: "#2a2a2a", marginLeft: "20px" }}
        >
          {contentToRender}
        </ChatbotMessage>
      );
    } catch (e) {
      return (
        <ChatbotMessage
          isCasual={true}
          style={{ backgroundColor: "#2a2a2a", marginLeft: "20px" }}
        >
          {contentToRender}
        </ChatbotMessage>
      );
    }
  };
 const renderSocialMediaContent = (content) => {
    const contentToRender = (content && typeof content === 'object' && content.content) ? content.content : content;
    try {
      const suggestions = JSON.parse(contentToRender);
      if (Array.isArray(suggestions)) {
        return (
          <ChatbotSuggestions>
            <SuggestionsHeader>Propozycje treści:</SuggestionsHeader>
            <SuggestionsBox>
              {suggestions.map((suggestion, index) => (
                <SuggestionItem key={index} isHistory={true}>
                  {suggestion.title && (
                    <SuggestionTitle>{suggestion.title}</SuggestionTitle>
                  )}
                  <SuggestionDescription>
                    {suggestion.description}
                  </SuggestionDescription>
                </SuggestionItem>
              ))}
            </SuggestionsBox>
          </ChatbotSuggestions>
        );
      }
      return <ChatbotMessage>{contentToRender}</ChatbotMessage>;
    } catch (e) {
      return <ChatbotMessage>{contentToRender}</ChatbotMessage>;
    }
  };

  const renderFacebookContent = (content) => {
    const contentToRender = (content && typeof content === 'object' && content.content) ? content.content : content;
    try {
      const suggestions = JSON.parse(contentToRender);
      if (Array.isArray(suggestions)) {
        return (
          <ChatbotSuggestions>
            <SuggestionsHeader>Pomysły:</SuggestionsHeader>
            <SuggestionsBox>
              {suggestions.map((suggestion, index) => (
                <SuggestionItem key={index} isHistory={true}>
                  <SuggestionTitle isFacebook={true}>
                    {suggestion.title}
                  </SuggestionTitle>
                  <SuggestionDescription isFacebook={true}>
                    {suggestion.description || "Brak opisu dla tej sugestii."}
                  </SuggestionDescription>
                </SuggestionItem>
              ))}
            </SuggestionsBox>
          </ChatbotSuggestions>
        );
      }
      return <ChatbotMessage isFacebook={true}>{contentToRender}</ChatbotMessage>;
    } catch (e) {
      return <ChatbotMessage isFacebook={true}>{contentToRender}</ChatbotMessage>;
    }
  };

const renderImageContent = (content) => {
  const contentToRender = (content && typeof content === 'object' && content.content) ? content.content : content;
  // wyciągamy src i alt z naszego zapisanego w historii <img>
  const match = 
    typeof contentToRender === "string" && 
    contentToRender.match(/<img src="([^"]+)" alt="([^"]*)" \/>/);

  if (match) {
    const [, src, alt] = match;
   return (
      <ChatbotMessage isImage={true} style={{ textAlign: "center", padding: 0, background: "none", marginTop: 0 }}>
        <HistoryImage src={src} alt={alt} />
      </ChatbotMessage>
    );
  }

  // fallback na wypadek, gdyby nie był to <img>
  return (
    <ChatbotMessage style={{ textAlign: "center" }}>
      {contentToRender}
    </ChatbotMessage>
  );
};

const renderVideoContent = (content) => {
  const contentToRender = (content && typeof content === 'object' && content.content) ? content.content : content;
  // Sprawdź czy content zawiera URL do wideo
  const urlMatch = typeof contentToRender === 'string' && contentToRender.match(/https:\/\/[^\s]+\.mp4[^\s]*/);
  
  if (urlMatch) {
    const videoUrl = urlMatch[0];
    return (
      <div style={{ marginTop: '10px' }}>
        <HistoryVideo src={videoUrl} />
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
          Wygenerowane wideo
        </div>
      </div>
    );
  }

  // Fallback - pokaż text content
  return (
    <ChatbotMessage>
      {contentToRender}
    </ChatbotMessage>
  );
};

const renderAdvertisementContent = (content) => {
  const contentToRender = (content && typeof content === 'object' && content.content) ? content.content : content;
  try {
    const suggestions = JSON.parse(contentToRender);
    if (Array.isArray(suggestions)) {
      return (
        <ChatbotSuggestions>
          <SuggestionsHeader>Propozycje ogłoszeń:</SuggestionsHeader>
          <SuggestionsBox>
            {suggestions.map((suggestion, index) => (
              <SuggestionItem key={index} isHistory={true}>
                <SuggestionTitle>{suggestion.title}</SuggestionTitle>
                <SuggestionDescription>
                  {suggestion.description}
                </SuggestionDescription>
              </SuggestionItem>
            ))}
          </SuggestionsBox>
        </ChatbotSuggestions>
      );
    }
    return <ChatbotMessage>{contentToRender}</ChatbotMessage>;
  } catch (e) {
    return <ChatbotMessage>{contentToRender}</ChatbotMessage>;
  }
};

const renderArticlesContent = (content) => {
  const contentToRender = (content && typeof content === 'object' && content.content) ? content.content : content;
  try {
    const suggestions = JSON.parse(contentToRender);
    if (Array.isArray(suggestions)) {
      return (
        <ChatbotSuggestions>
          <SuggestionsHeader>Propozycje tytułów artykułów:</SuggestionsHeader>
          <SuggestionsBox>
            {suggestions.map((suggestion, index) => (
              <SuggestionItem key={index} isHistory={true}>
                <SuggestionTitle>{suggestion.title || suggestion}</SuggestionTitle>
                {suggestion.description && (
                  <SuggestionDescription>
                    {suggestion.description}
                  </SuggestionDescription>
                )}
              </SuggestionItem>
            ))}
          </SuggestionsBox>
        </ChatbotSuggestions>
      );
    }
    return <ChatbotMessage>{contentToRender}</ChatbotMessage>;
  } catch (e) {
    return <ChatbotMessage>{contentToRender}</ChatbotMessage>;
  }
};

const renderMusicContent = (content) => {
  const contentToRender = (content && typeof content === 'object' && content.content) ? content.content : content;
  try {
    const musicData = JSON.parse(contentToRender);
    
    if (musicData.type === 'music') {
      // Jeśli mamy wiele utworów
      if (musicData.tracks && musicData.tracks.length > 1) {
        return (
          <MultiMusicPlayer 
            musicResult={musicData}
            onClose={null}
            hideCloseButton={true} // Historia - zawsze ukryj krzyżyk
          />
        );
      } else {
        // Pojedynczy utwór
        return (
          <MusicPlayer
            audioUrl={musicData.audioUrl}
            title={musicData.title}
            duration={musicData.duration}
            onClose={null}
            hideCloseButton={true}
          />
        );
      }
    }
  } catch (error) {
    console.log('🎵 Not JSON, checking for other music content types...');
    
    // Fallback 1: Sprawdź czy to URL audio (stare wpisy)
    const musicUrl = typeof contentToRender === 'string' && contentToRender.match(/https:\/\/storage\.googleapis\.com\/[^\s]+\.(mp3|wav|m4a|ogg)([^\s]*)?/i);
    
    if (musicUrl) {
      const audioSrc = musicUrl[0];
      return (
        <MusicPlayer
          audioUrl={audioSrc}
          title="Wygenerowana muzyka AI"
          duration={null}
          onClose={null}
          hideCloseButton={true}
        />
      );
    }
    
    // Fallback 2: Sprawdź czy to wpis o rozpoczęciu/błędzie
    if (typeof contentToRender === 'string' && (contentToRender.includes('Rozpoczęto generowanie:') || 
        contentToRender.includes('Muzyka:') ||
        contentToRender.includes('Błąd') ||
        contentToRender.includes('Error'))) {
      return (
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '8px',
          color: '#666',
          fontStyle: 'italic'
        }}>
          🎵 {contentToRender}
        </div>
      );
    }
  }
  
  // Ostateczny fallback
  return <div dangerouslySetInnerHTML={{ __html: contentToRender }} />;
};

  if (initialLoading || isLoading === "loading") {
    return <LoadingPage />;
  }

  let returned;

  switch (isLoading) {
    case "success":
      if (!user) {
        returned = <LoadingPage isHighZIndex={true} />;
      } else if (tilesDetails.length > 0 && user) {
        returned = (
          <ChatbotContainer>
            <ChatbotMain
              isDetailsExpanded={isDetailsExpanded}
              isDetailsPanelVisible={true}
            >
              <HistoryHeaderContainer>
                <AppTitle>Historia Rozmów</AppTitle>
                {chatHistory && chatHistory.length > 0 && (
                  <HistoryActionsContainer>
                    <CategorySelect
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                    >
                      <option value="all">Wszystkie kategorie</option>
                      {chatbots.map((chatbot) => (
                        <option key={chatbot.id} value={chatbot.id}>
                          {chatbot.name}
                        </option>
                      ))}
                    </CategorySelect>
                    <SelectAllButton onClick={handleSelectAll}>
                      {filteredHistory.every((entry) => selectedEntries.has(entry.id)) && filteredHistory.length > 0
                        ? "Odznacz wszystko"
                        : "Zaznacz wszystko"}
                    </SelectAllButton>
                    <DeleteButton
                      onClick={handleDeleteClick}
                      disabled={selectedEntries.size === 0 || isDeleting}
                    >
                      <FaTrash />
                      Usuń {selectedEntries.size > 0 && `(${selectedEntries.size})`}
                    </DeleteButton>
                  </HistoryActionsContainer>
                )}
              </HistoryHeaderContainer>
              {filteredHistory && filteredHistory.length > 0 ? (
                <SuggestionsBox>
                  {filteredHistory.map((entry) => (
                    <HistoryTileWrapper
                      key={entry.id}
                      onClick={(e) => handleTileClick(entry, e)}
                    >
                      <HistoryTileContent>
                        <HistoryTileCheckbox
                          type="checkbox"
                          checked={selectedEntries.has(entry.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleCheckboxChange(entry.id, e.target.checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div style={{ flex: 1 }}>
                          <SuggestionTitle>{entry.title}</SuggestionTitle>
                          <SuggestionDescription>
                            {(() => {
                              const ts = entry.timestamp;
                              // Obsługa: Firestore Timestamp, string ISO, number (ms)
                              try {
                                const date = ts && typeof ts.toDate === 'function'
                                  ? ts.toDate()
                                  : typeof ts === 'string'
                                  ? new Date(ts)
                                  : typeof ts === 'number'
                                  ? new Date(ts)
                                  : null;
                                return date ? date.toLocaleString() : '';
                              } catch (e) {
                                return '';
                              }
                            })()}
                            <ChatbotName
                              isLawyer={entry.chatbotId === "lawyer"}
                              isCasual={entry.chatbotId === "casual"}
                               isFacebook={entry.chatbotId === "facebook"}
                               isImage={entry.chatbotId === "image"}
                               isSocialMedia={entry.chatbotId === "advanced"}
                             isDiet={entry.chatbotId === "diet"}
                             isSearchEngine={entry.chatbotId === "searchengine"}
                             isAdvertisement={entry.chatbotId === "advertisement"}
                             isArticles={entry.chatbotId === "articles"}
                            >
                              {entry.chatbotId === "reels"
                                ? "Filmiki i Rolki"
                                : entry.chatbotId === "advanced"
                                ? "Social Media"
                                : entry.chatbotId === "lawyer"
                                ? "Asystent Prawny"
                                :entry.chatbotId === 'music' 
                                ? "Asystent Muzyczny"
                                 : entry.chatbotId === "facebook"
                                 ? "Posty Facebook"
                                 : entry.chatbotId === "image"
                                 ? "Lokalny Artysta"
                                   : entry.chatbotId === "video" 
  ? "Generator Wideo"          
                                  : entry.chatbotId === "diet"
                                  ? "Doradca Dietetyczny"
                                  : entry.chatbotId === "searchengine"
                                  ? "Wyszukiwarka"
                                  : entry.chatbotId === "advertisement"
                                  ? "Twórca Ogłoszeń"
                                  : entry.chatbotId === "articles"
                                  ? "Twórca Artykułów"
                                : "Asystent Ogólny"}
                            </ChatbotName>
                          </SuggestionDescription>
                        </div>
                      </HistoryTileContent>
                    </HistoryTileWrapper>
                  ))}
                </SuggestionsBox>
              ) : (
                <ChatbotMessage>
                  {selectedCategory === "all" 
                    ? "Brak historii rozmów." 
                    : `Brak historii dla kategorii: ${chatbots.find(c => c.id === selectedCategory)?.name || selectedCategory}`}
                </ChatbotMessage>
              )}
            </ChatbotMain>
            <ChatbotHistoryDetailsPanel isDetailsExpanded={isDetailsExpanded}>
              <ChatbotDetailsHeader isDetailsExpanded={isDetailsExpanded} />
              <ChatbotExpandButton
                isDetailsExpanded={isDetailsExpanded}
                onClick={toggleDetailsPanel}
              >
                {isDetailsExpanded ? "Zwiń szczegóły" : "Rozwiń szczegóły"}
              </ChatbotExpandButton>
              {selectedEntry ? (
                entryLoading ? (
                  <LoadingElement>Ładowanie</LoadingElement>
                ) : entryDetails ? (
                  <EntryDetailsContainer>
                    <EntryDetailsTitle>{entryDetails.title}</EntryDetailsTitle>
                 {selectedEntry.chatbotId === "image" ? (
      renderImageContent(entryDetails.content)
      ) : selectedEntry.chatbotId === "video" ? (  
  renderVideoContent(entryDetails.content)   
                  ) : selectedEntry.chatbotId === "reels" ? (
                    renderReelsContent(entryDetails.content)
                  ) : selectedEntry.chatbotId === "advanced" ? (
                    renderSocialMediaContent(entryDetails.content)
                   ) : selectedEntry.chatbotId === "searchengine" ? (
                    renderSearchEngineContent(entryDetails.content)
                  ) : selectedEntry.chatbotId === "facebook" ? (
                    renderFacebookContent(entryDetails.content)
                  ) :  selectedEntry.chatbotId === "music" ? (
  renderMusicContent(entryDetails.content) ) :
                  selectedEntry.chatbotId === "diet" ? (
                    renderDietContent(entryDetails.content)
                  ) : selectedEntry.chatbotId === "lawyer" ? (
                    renderLawyerContent(entryDetails.content)
                  ) : selectedEntry.chatbotId === "advertisement" ? (
                    renderAdvertisementContent(entryDetails.content)
                  ) : selectedEntry.chatbotId === "articles" ? (
                    renderArticlesContent(entryDetails.content)
                  ) : (
                    renderCasualContent(entryDetails.content)
                  )}
                  </EntryDetailsContainer>
                ) : (
                  <ChatbotMessage>Nie znaleziono wpisu.</ChatbotMessage>
                )
              ) : (
                <ChatbotMessage>
                  Wybierz wpis z historii, aby zobaczyć szczegóły.
                </ChatbotMessage>
              )}
            </ChatbotHistoryDetailsPanel>
          </ChatbotContainer>
        );
      } else {
        returned = handleUnauthorizedAccess();
      }
      break;
    case "error":
      returned = <ErrorPage />;
      break;
    default:
      returned = <LoadingPage />;
  }

  return (
    <>
      {returned}
      <DeleteConfirmModal
        show={showDeleteModal}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isDeletingAll={selectedEntries.size === chatHistory.length}
      />
    </>
  );
};

export default ChatbotHistory;